import { APP_THEME } from "@/data/constants";
import { create } from "zustand";
import { devtools } from "zustand/middleware";

type TState = {
  authToken: string;
  billType: "monthly" | "yearly";
  upgradeStep: number;
  currentRanking: number;
  selectedProductId: string;
  upgradePrice: number;
  credits: number;
  couponValue: string;
  error: boolean;
  successMsg: boolean;
  estimateSuccess: boolean;
  isUpgradeModalOpen: boolean;
  isIntervalModalOpen: boolean;
  pricingTheme: "dark" | "light";
  isPricingRefetching: boolean;
  isAnnualBenefitsExpanded: boolean;

  pricingPromoCode: string | undefined; // code from sale, or referrer
  showReferredPricing: boolean;
};

type TActions = {
  setAuthToken: (token: string) => void;
  setBillType: (billType: "monthly" | "yearly") => void;
  setUpgradeStep: (step: number) => void;
  setCurrentRanking: (ranking: number) => void;
  setSelectedProductId: (id: string) => void;
  setUpgradePrice: (price: number) => void;
  setCredits: (credits: number) => void;
  setIsIntervalModalOpen: (isOpen: boolean) => void;
  setCouponValue: (value: string) => void;
  setError: (value: boolean) => void;
  setSuccessMsg: (value: boolean) => void;
  setUpgradeModalOpen: (isOpen: boolean) => void;
  setEstimateSuccess: (value: boolean) => void;
  setPricingTheme: (theme: "dark" | "light") => void;
  setIsPricingRefetching: (state: boolean) => void;
  setPricingPromoCode: (promoCode: string | undefined) => void;
  setShowReferredPricing: (state: boolean) => void;
  setIsAnnualBenefitsExpanded: (state: boolean) => void;
};

type TPricingStore = TState & TActions;

const usePricingStore = create<TPricingStore>()(
  devtools(
    (set) => ({
      authToken: "",
      setAuthToken: (token) => set({ authToken: token }),

      billType: "yearly",
      setBillType: (billType) =>
        set({ billType, isAnnualBenefitsExpanded: billType === "yearly" }),

      upgradeStep: 1,
      setUpgradeStep: (step) => set({ upgradeStep: step }),

      currentRanking: -1,
      setCurrentRanking: (ranking) => set({ currentRanking: ranking }),

      selectedProductId: "",
      setSelectedProductId: (id) => set({ selectedProductId: id }),

      upgradePrice: 0,
      setUpgradePrice: (price) => set({ upgradePrice: price }),

      credits: 0,
      setCredits: (credits) => set({ credits }),

      couponValue: "",
      setCouponValue: (value) => set({ couponValue: value }),

      error: false,
      setError: (value) => set({ error: value }),

      successMsg: false,
      setSuccessMsg: (value) => set({ successMsg: value }),

      estimateSuccess: false,
      setEstimateSuccess: (value) => set({ estimateSuccess: value }),

      isUpgradeModalOpen: false,
      setUpgradeModalOpen: (isOpen) => set({ isUpgradeModalOpen: isOpen }),

      isIntervalModalOpen: false,
      setIsIntervalModalOpen: (isOpen) => set({ isIntervalModalOpen: isOpen }),

      pricingTheme: APP_THEME.LIGHT,
      setPricingTheme: (theme) => set({ pricingTheme: theme }),

      isPricingRefetching: false,
      setIsPricingRefetching: (state) => set({ isPricingRefetching: state }),

      pricingCardFooterHeight: 0,

      pricingPromoCode: undefined,
      setPricingPromoCode: (promoCode) => set({ pricingPromoCode: promoCode }),

      showReferredPricing: false,
      setShowReferredPricing: (state) => set({ showReferredPricing: state }),

      isAnnualBenefitsExpanded: true,
      setIsAnnualBenefitsExpanded: (state) =>
        set({ isAnnualBenefitsExpanded: state }),
    }),
    { name: "Pricing Store" },
  ),
);

export default usePricingStore;
